/* Admin > People — expense-user PROVISIONING (Phase 1). Wired to /v1/expense/admin/users. Turns a
   CIAM user into a provisioned expense user by creating their expense_user_profile (a manager for
   approval routing — required before they can submit a report). "Provision me" self-onboards the
   signed-in user (server defaults to the token oid); the form provisions anyone by oid. */
import { UserPlusIcon } from '@phosphor-icons/react';
import { type FormEvent, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import {
  useExpenseUsers,
  useProvisionUser,
  type ExpenseUserProfile,
} from './hooks/use-expense-users';
import { Field, Input } from './ui/field';

function shortOid(oid: string | null): string {
  return oid ? oid.slice(0, 8) : '—';
}

export function AdminPeople() {
  const { data: users = [], isLoading, isError } = useExpenseUsers();
  const provision = useProvisionUser();

  const [userOid, setUserOid] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [managerUserOid, setManagerUserOid] = useState('');
  const [approvalThreshold, setApprovalThreshold] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(null);
    provision.mutate(
      {
        userOid: userOid || undefined,
        displayName: displayName || undefined,
        email: email || undefined,
        managerUserOid: managerUserOid || undefined,
        approvalThreshold: approvalThreshold || undefined,
      },
      {
        onSuccess: (p) => {
          setDone(`Provisioned ${p.displayName ?? shortOid(p.userOid)}.`);
          setUserOid('');
          setDisplayName('');
          setEmail('');
          setManagerUserOid('');
          setApprovalThreshold('');
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Provisioning failed.'),
      },
    );
  };

  const provisionMe = () => {
    setError(null);
    setDone(null);
    provision.mutate(
      { managerUserOid: managerUserOid || undefined },
      {
        onSuccess: () => setDone('Provisioned your own expense profile.'),
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Provisioning failed.'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-sans font-semibold text-content-primary text-base">
            Provision an expense user
          </h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={provision.isPending}
            onClick={provisionMe}
          >
            <UserPlusIcon className="me-2 size-icon-sm" />
            Provision me
          </Button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="User OID"
              htmlFor="p-oid"
              hint="Blank = the signed-in user (self-onboard)"
            >
              <Input
                id="p-oid"
                value={userOid}
                onChange={(e) => setUserOid(e.target.value)}
                placeholder="Entra object id"
              />
            </Field>
            <Field label="Display name" htmlFor="p-name">
              <Input
                id="p-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Traveler"
              />
            </Field>
            <Field label="Email" htmlFor="p-email">
              <Input
                id="p-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@cbtravel.com"
              />
            </Field>
            <Field
              label="Manager OID"
              htmlFor="p-mgr"
              hint="Required before this user can submit"
            >
              <Input
                id="p-mgr"
                value={managerUserOid}
                onChange={(e) => setManagerUserOid(e.target.value)}
                placeholder="approver's object id"
              />
            </Field>
            <Field label="Second-approver threshold" htmlFor="p-thr">
              <Input
                id="p-thr"
                type="number"
                step="0.01"
                min="0"
                value={approvalThreshold}
                onChange={(e) => setApprovalThreshold(e.target.value)}
                placeholder="e.g. 500"
              />
            </Field>
          </div>
          {error && (
            <p className="font-inter text-content-danger text-sm">{error}</p>
          )}
          {done && (
            <p className="font-inter text-content-success text-sm">{done}</p>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={provision.isPending}
            >
              <UserPlusIcon className="me-2 size-icon-sm" />
              Provision user
            </Button>
          </div>
        </form>
      </Card>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u: ExpenseUserProfile) => (
              <TableRow key={u.userOid} data-user-row>
                <TableCell className="font-inter font-medium text-content-primary">
                  {u.displayName ?? '—'}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {u.email ?? '—'}
                </TableCell>
                <TableCell className="text-content-secondary">
                  <code className="font-mono text-xs">
                    {shortOid(u.userOid)}
                  </code>
                </TableCell>
                <TableCell className="text-content-secondary">
                  <code className="font-mono text-xs">
                    {shortOid(u.managerUserOid)}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={u.active ? 'success' : 'default'}
                    intent="background"
                    borderRadius="round"
                  >
                    {u.active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            {isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-secondary"
                >
                  Loading people…
                </TableCell>
              </TableRow>
            )}
            {isError && !isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-danger"
                >
                  Couldn’t load provisioned users.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && users.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-secondary"
                >
                  No one provisioned yet. Provision a user above.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
